import type { SvgIconComponent } from '@mui/icons-material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import { paths } from '@/routes/paths';
import {
  activeItemId,
  collectLinkItems,
  isNavPathActive,
  type SidebarBadgeKey,
  type SidebarLinkItem,
  type SidebarSection,
} from './sidebarNav';

/**
 * Aree di lavoro.
 *
 * La sidebar non elenca più ogni pagina allo stesso livello: ogni ruolo vede
 * solo le aree che hanno almeno una destinazione visibile. Gli id sono le
 * foglie già definite in `sidebarNav` — i permessi restano lì.
 */
export interface WorkspaceSubsectionDef {
  id: string;
  label: string;
  linkIds: readonly string[];
}

export interface WorkspaceDef {
  id: string;
  label: string;
  description: string;
  icon: SvgIconComponent;
  /** Voci dell'area fuori dalle sottosezioni. In pagina compaiono dopo i gruppi. */
  linkIds: readonly string[];
  /** Gruppi etichettati dentro l'area. Gli id non vanno ripetuti in `linkIds`. */
  subsections?: readonly WorkspaceSubsectionDef[];
  /** Pagine dell'area senza voce propria, per esempio l'archivio aperto dai clienti. */
  matchPaths?: readonly string[];
}

export interface ResolvedSubsection {
  id: string;
  label: string;
  links: SidebarLinkItem[];
}

export interface ResolvedWorkspace {
  id: string;
  label: string;
  description: string;
  icon: SvgIconComponent;
  /** Tutte le voci, sottosezioni incluse, nell'ordine di lettura. */
  links: SidebarLinkItem[];
  subsections: ResolvedSubsection[];
  matchPaths: readonly string[];
}

const WORKSPACES: readonly WorkspaceDef[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Code operative, panoramica e report',
    icon: SpaceDashboardOutlinedIcon,
    linkIds: ['dashboard-operativa', 'panoramica', 'report'],
  },
  {
    id: 'servizi',
    label: 'Servizi',
    description: 'Moduli attivi e stato dei servizi',
    icon: HubOutlinedIcon,
    linkIds: ['servizi'],
  },
  {
    id: 'operazioni',
    label: 'Operazioni',
    description: 'Ticket, attività, calendario e alert',
    icon: ShieldOutlinedIcon,
    linkIds: [
      'coda-ticket',
      'ticket',
      'ticket-aperti',
      'ticket-nuovo',
      'ticket-chiusi',
      'attivita',
      'lead-calendario',
      'calendario-portale',
      'sicurezza-staff',
      'backup-alert',
    ],
  },
  {
    id: 'clienti',
    label: 'Clienti',
    description: 'Anagrafica, lead e matrice',
    icon: BusinessOutlinedIcon,
    linkIds: ['clienti-elenco', 'framework-matrix'],
    matchPaths: [paths.admin.clientiPersi],
  },
  {
    id: 'infrastruttura',
    label: 'Infrastruttura',
    description: 'Macchine, salute e backup',
    icon: DnsOutlinedIcon,
    linkIds: ['macchine-elenco', 'macchine-stato-salute', 'backup-job', 'backup-pending'],
  },
  {
    id: 'commerciale',
    label: 'Commerciale',
    description: 'Lead e preventivi',
    icon: RequestQuoteOutlinedIcon,
    linkIds: [
      'lead-elenco',
      'preventivi-admin',
      'preventivi-mensili-admin',
      'preventivi-portale',
      'cedolini-portale',
      'contratti-portale',
    ],
  },
  {
    id: 'amministrazione',
    label: 'Amministrazione',
    description: 'Cedolini, contratti e fatture',
    icon: AccountBalanceOutlinedIcon,
    linkIds: ['cedolini-admin', 'contratti-elenco', 'fatture-admin'],
  },
  {
    id: 'conoscenza',
    label: 'Wiki',
    description: 'Documenti e cartelle cliente',
    icon: FolderSharedOutlinedIcon,
    linkIds: [
      'documenti-elenco',
      'documentazione-interna',
      'documenti-visibilita',
      'documenti-portale-elenco',
      'documenti-portale-visibilita',
    ],
  },
  {
    id: 'azienda',
    label: 'Azienda',
    description: 'Organizzazione e persone',
    icon: AccountTreeOutlinedIcon,
    linkIds: ['organizzazione', 'utenti'],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    description: 'Catalogo, accessi e integrazioni',
    icon: SettingsOutlinedIcon,
    linkIds: ['utenti-staff', 'vaultwarden'],
    subsections: [
      {
        id: 'catalogo',
        label: 'Catalogo',
        linkIds: ['catalogo-prodotti', 'catalogo-servizi', 'categorie-ticket'],
      },
    ],
  },
];

/** Restano nel menu account della topbar, non nella colonna di lavoro. */
const ACCOUNT_LINK_IDS = new Set(['notifiche', 'profilo']);

function linksFor(byId: ReadonlyMap<string, SidebarLinkItem>, ids: readonly string[]): SidebarLinkItem[] {
  return ids.flatMap((id) => {
    const link = byId.get(id);
    return link ? [link] : [];
  });
}

export function resolveWorkspaces(sections: readonly SidebarSection[]): ResolvedWorkspace[] {
  const byId = new Map(collectLinkItems(sections).map((link) => [link.id, link]));
  const used = new Set<string>();
  const resolved: ResolvedWorkspace[] = [];

  for (const def of WORKSPACES) {
    const subsections = (def.subsections ?? []).flatMap((subsection) => {
      const links = linksFor(byId, subsection.linkIds);
      if (links.length === 0) return [];
      return [{ id: subsection.id, label: subsection.label, links }];
    });
    const loose = linksFor(byId, def.linkIds);
    const links = [...subsections.flatMap((subsection) => subsection.links), ...loose];
    for (const link of links) used.add(link.id);
    if (links.length === 0) continue;
    resolved.push({
      id: def.id,
      label: def.label,
      description: def.description,
      icon: def.icon,
      links,
      subsections,
      matchPaths: def.matchPaths ?? [],
    });
  }

  const leftover = collectLinkItems(sections).filter((link) => !used.has(link.id) && !ACCOUNT_LINK_IDS.has(link.id));
  if (leftover.length > 0) {
    resolved.push({
      id: 'altro',
      label: 'Altro',
      description: 'Destinazioni non ancora raggruppate',
      icon: MoreHorizOutlinedIcon,
      links: leftover,
      subsections: [],
      matchPaths: [],
    });
  }

  return resolved;
}

function sharedPrefixScore(pathname: string, target: string): number {
  const left = pathname.split('/').filter(Boolean);
  const right = target.split('?')[0]?.split('/').filter(Boolean) ?? [];
  let index = 0;
  while (index < left.length && index < right.length && left[index] === right[index]) index += 1;
  return index;
}

/** Area che contiene la pagina corrente. Le schede dettaglio ereditano l'area del percorso più vicino. */
export function activeWorkspaceId(pathname: string, workspaces: readonly ResolvedWorkspace[]): string | null {
  const links = workspaces.flatMap((workspace) => workspace.links);
  const currentId = activeItemId(pathname, links);
  if (currentId) {
    return workspaces.find((workspace) => workspace.links.some((link) => link.id === currentId))?.id ?? null;
  }

  const hinted = workspaces.find((workspace) =>
    workspace.matchPaths.some((path) => isNavPathActive(pathname, path)),
  );
  if (hinted) return hinted.id;

  let best: { id: string; score: number } | null = null;
  for (const workspace of workspaces) {
    for (const link of workspace.links) {
      const score = sharedPrefixScore(pathname, link.to);
      if (score > 0 && (best == null || score > best.score)) best = { id: workspace.id, score };
    }
  }
  return best?.id ?? null;
}

/** Somma i badge distinti dell'area, senza contare due volte la stessa coda. */
export function workspaceAlertCount(
  links: readonly SidebarLinkItem[],
  badges: Partial<Record<SidebarBadgeKey, number>>,
): number {
  const seen = new Set<SidebarBadgeKey>();
  let total = 0;
  for (const link of links) {
    if (!link.badge || seen.has(link.badge)) continue;
    seen.add(link.badge);
    const count = badges[link.badge] ?? 0;
    if (count > 0) total += count;
  }
  return total;
}
